// The Dunes: the south-middle section. A gravel plain with acacias behind
// the beach, giving way southward to a sea of crescent dunes with rippled lee
// sides; the river from the Peaks cuts a red canyon across it on its way to
// the sea, with flat-topped mesas standing near its rim; and a spring-fed
// oasis ringed with palms. No places yet: land to wander.
//
// Its own random streams (seed + 800 and up), consumed in full whatever region
// is being painted.
import { rng, noise2, softShadow, wash, blobPath, inkPoly, inkEllipse, line, withAlpha, mix, hatch, samplePath } from "./paint"
import { paintTuft, paintBush, paintRock, paintReeds } from "./nature"
import { paintPalm } from "./isles"

export function buildDunes(config, { x0, y0, x1, y1 }) {
    const seed = config.seed
    const active = !!config.sections?.some((s) => s.key === "dunes")
    const n = noise2(seed + 800), n2 = noise2(seed + 801)
    const w = x1 - x0, h = y1 - y0
    const oasis = active ? config.oasis : null
    const inOasis = (x, y, pad = 0) => !!oasis && ((x - oasis.x) / (oasis.rx + pad)) ** 2 + ((y - oasis.y) / (oasis.ry + pad)) ** 2 < 1
    const beaches = active ? [config.south_coast, config.dunes_west_coast].filter(Boolean).map((pts) => samplePath(pts, 8)) : []
    // the canyon follows the river where it crosses the Dunes
    const river = active && config.south_river ? samplePath(config.south_river.points, 6) : []
    const canyon = river.filter((p) => p.x < x1 + 40 && p.x > x0 - 40 && p.y > y0)
    const nearCanyon = (() => {
        const cell = 80, map = new Map()
        canyon.forEach((p) => { const k = Math.floor(p.x / cell) * 100003 + Math.floor(p.y / cell); if (!map.has(k)) map.set(k, []); map.get(k).push(p) })
        return (x, y, r) => {
            for (let cx = Math.floor((x - r) / cell); cx <= Math.floor((x + r) / cell); cx++) for (let cy = Math.floor((y - r) / cell); cy <= Math.floor((y + r) / cell); cy++) {
                const b = map.get(cx * 100003 + cy)
                if (b) for (let i = 0; i < b.length; i++) if (Math.hypot(b[i].x - x, b[i].y - y) < r) return true
            }
            return false
        }
    })()
    const CANYON = 150
    const inside = (x, y) => active && x >= x0 && x < x1 && y >= y0 && y < y1
    // how far into the dune sea: 0 on the gravel plain, 1 deep in the dunes
    const duneness = (x, y) => Math.max(0, Math.min(1, (y - (y0 + 2200)) / 1600 + (n(x / 900, y / 900) - 0.5) * 0.7))

    function biome(x, y) {
        if (inOasis(x, y, 140)) return "oasis"
        if (nearCanyon(x, y, CANYON + 40)) return "canyon"
        return "desert"
    }

    // --- the land -----------------------------------------------------------------

    function paintLand(ctx, pal, vis) {
        if (!active) return
        const area = (w * h) / (2000 * 1250)
        ctx.save()
        // the sand runs on under the Wildwood's ragged edge, so no gap shows
        ctx.beginPath(); ctx.rect(x0 - 2, y0 - 2, w + 600, h + 42); ctx.clip()
        ctx.fillStyle = pal.dune; ctx.fillRect(x0 - 2, y0 - 2, w + 600, h + 42)
        const r = rng(seed + 802)
        // washes: pale sand on the rises, darker gravel on the plain
        for (let i = 0; i < 70 * area; i++) {
            const x = x0 + r() * w, y = y0 + r() * h, rad = 100 + r() * 260, a = 0.3 + r() * 0.3
            if (!vis(x, y, rad)) continue
            const d = duneness(x, y), v = n2(x / 300, y / 300)
            wash(ctx, x, y, rad, d < 0.3 ? (v > 0.5 ? pal.scrub : pal.duneShade) : v > 0.5 ? pal.duneLight : pal.duneShade, a * (d < 0.3 ? 0.55 : 0.7))
        }
        // grit on the plain, grain in the dunes
        for (let i = 0; i < 6000 * area; i++) {
            const x = x0 + r() * w, y = y0 + r() * h, kind = r()
            if (!vis(x, y, 4)) continue
            const d = duneness(x, y)
            ctx.fillStyle = d < 0.35 ? withAlpha(kind < 0.5 ? pal.duneDeep : pal.redRockDark, 0.35) : withAlpha(kind < 0.5 ? pal.duneShade : pal.duneLight, 0.5)
            ctx.beginPath(); ctx.arc(x, y, d < 0.35 ? 1 + kind : 0.8, 0, Math.PI * 2); ctx.fill()
        }
        // crescent dunes: a shaded lee face under a bright crest, the wind from the north-east
        const rd = rng(seed + 803)
        for (let i = 0; i < 230 * (area / 16); i++) {
            const x = x0 + rd() * w, y = y0 + 1600 + rd() * (h - 1600), s = 60 + rd() * 150, tilt = 0.35 + (rd() - 0.5) * 0.4, sd = rd()
            if (!vis(x, y, s * 1.3) || duneness(x, y) < 0.25 + sd * 0.4 || nearCanyon(x, y, CANYON + s * 0.6) || inOasis(x, y, 200)) continue
            crescent(ctx, pal, x, y, s, tilt)
        }
        // ripples in the dune sea
        for (let i = 0; i < 2600 * (area / 16); i++) {
            const x = x0 + rd() * w, y = y0 + rd() * h, len = 12 + rd() * 22
            if (!vis(x, y, len) || duneness(x, y) < 0.35 || nearCanyon(x, y, CANYON + 20) || inOasis(x, y, 160)) continue
            ctx.strokeStyle = withAlpha(pal.duneShade, 0.55); ctx.lineWidth = 1
            ctx.beginPath(); ctx.moveTo(x - len / 2, y); ctx.quadraticCurveTo(x, y - 3, x + len / 2, y + 1); ctx.stroke()
        }
        // the canyon: red rock rims, strata, a sandy floor for the river
        if (canyon.some((p) => vis(p.x, p.y, 300))) {
            // the canyon widens and narrows as it winds (stamped, so its walls wander)
            const widthAt = (i) => 0.78 + n(i / 26, 5.5) * 0.5
            const along = (width, color, alpha = 1) => {
                ctx.fillStyle = withAlpha(color, alpha)
                canyon.forEach((p, i) => { if (i % 2 || !vis(p.x, p.y, width)) return; ctx.beginPath(); ctx.arc(p.x, p.y, (width / 2) * widthAt(i), 0, Math.PI * 2); ctx.fill() })
            }
            along(CANYON * 2 + 60, pal.redRockDark, 0.35)
            along(CANYON * 2 + 20, pal.redRock)
            along(CANYON * 2 - 30, pal.redRockLight)
            // strata: broken lines along both walls
            const rs = rng(seed + 804)
            ;[0.9, 0.72, 0.55].forEach((k, band) => {
                ;[-1, 1].forEach((side) => {
                    ctx.strokeStyle = withAlpha(band === 0 ? pal.redRockDark : mix(pal.redRock, pal.redRockDark, 0.5), 0.7); ctx.lineWidth = band === 0 ? 2.2 : 1.2
                    ctx.beginPath()
                    let pen = false
                    canyon.forEach((p, i) => {
                        const wob = (n(i / 9, band + side * 3) - 0.5) * 22, gap = rs() < 0.08, wk = widthAt(i)
                        const x = p.x + p.nx * CANYON * k * wk * side + p.nx * wob, y = p.y + p.ny * CANYON * k * wk * side + p.ny * wob
                        if (gap) { pen = false; return }
                        if (!pen) { ctx.moveTo(x, y); pen = true } else ctx.lineTo(x, y)
                    })
                    ctx.stroke()
                })
            })
            along(CANYON - 10, pal.duneDeep)
            along(CANYON - 40, mix(pal.duneDeep, pal.dune, 0.4))
        }
        // the world's southern edge darkens
        const edge = 160
        const g = ctx.createLinearGradient(0, y1, 0, y1 - edge)
        g.addColorStop(0, withAlpha(pal.duneDeep, 0.8)); g.addColorStop(1, withAlpha(pal.duneDeep, 0))
        ctx.fillStyle = g; ctx.fillRect(x0, y1 - edge, w, edge)
        ctx.restore()
    }

    // Golden beaches along the Dunes' two shores, before the sea.
    function paintShore(ctx, pal, vis) {
        beaches.forEach((b) => {
            if (!b.some((p) => vis(p.x, p.y, 200))) return
            ctx.save()
            ctx.lineCap = "round"; ctx.lineJoin = "round"
            ;[[250, pal.duneLight], [90, pal.duneShade]].forEach(([width, color]) => {
                ctx.strokeStyle = color; ctx.lineWidth = width
                ctx.beginPath(); b.forEach((p, i) => (i ? ctx.lineTo(p.x, p.y) : ctx.moveTo(p.x, p.y))); ctx.stroke()
            })
            ctx.restore()
        })
    }

    // The oasis: a ring of green, then clear spring water.
    function paintAfterSea(ctx, pal, vis) {
        if (!oasis || !vis(oasis.x, oasis.y, oasis.rx + 200)) return
        const r = rng(seed + 805)
        blobPath(ctx, oasis.x, oasis.y + 6, oasis.rx + 150, oasis.ry + 90, seed + 806, 0.14, 30)
        ctx.fillStyle = withAlpha(pal.rain, 0.8); ctx.fill()
        blobPath(ctx, oasis.x, oasis.y + 4, oasis.rx + 60, oasis.ry + 36, seed + 807, 0.1, 30)
        ctx.fillStyle = pal.rainLight; ctx.fill()
        blobPath(ctx, oasis.x, oasis.y, oasis.rx, oasis.ry, seed + 808, 0.06, 30)
        const g = ctx.createRadialGradient(oasis.x - oasis.rx * 0.2, oasis.y - oasis.ry * 0.2, 6, oasis.x, oasis.y, oasis.rx)
        g.addColorStop(0, pal.lagoonDeep); g.addColorStop(0.7, pal.lagoon); g.addColorStop(1, mix(pal.lagoon, pal.foam, 0.4))
        ctx.fillStyle = g; ctx.fill()
        ctx.strokeStyle = pal.ink; ctx.lineWidth = 1.6; ctx.stroke()
        for (let i = 0; i < 40; i++) {
            const a = r() * Math.PI * 2, k = 1.05 + r() * 0.6, h = 4 + r() * 4
            ctx.lineWidth = 1.1; ctx.globalAlpha = 0.6
            paintTuft(ctx, r() < 0.5 ? pal.rainDark : pal.leafLight, oasis.x + Math.cos(a) * oasis.rx * k, oasis.y + Math.sin(a) * oasis.ry * k, h)
        }
        ctx.globalAlpha = 1
    }

    // --- nature ------------------------------------------------------------------------

    // `keep` clears what stands where something newer came (the railway,
    // the balloon field), after growing, so nothing else moves.
    function grow({ clearOf, spaced, note, inSea, keep = () => true }) {
        const sprites = [], footprints = [], trees = [], smalls = [], mesas = []
        if (!active) return { sprites, footprints, trees, smalls, mesas }
        const r = rng(seed + 820)
        // mesas near the canyon rim and out in the plain
        for (let i = 0; i < 400 && mesas.length < 9; i++) {
            const x = x0 + 300 + r() * (w - 600), y = y0 + 1200 + r() * (h - 1500), s = 90 + r() * 110, sd = Math.floor(r() * 1e6), near = r()
            if (inSea(x, y, 300) || inOasis(x, y, s + 250) || nearCanyon(x, y, CANYON + s * 0.9) || (near < 0.6 && !nearCanyon(x, y, CANYON + s * 2.4))) continue
            if (!clearOf(x, y, s * 0.95) || mesas.some((m) => Math.hypot(m.x - x, m.y - y) < (m.s + s) * 1.2)) continue
            mesas.push({ x, y, s, seed: sd })
        }
        mesas.forEach((m) => {
            if (!keep({ ...m, kind: "mountain" })) return
            sprites.push({ x: m.x, y: m.y, box: [-m.s * 1.1, -m.s * 0.95, m.s * 1.1, 10], kind: "mountain", sway: 0, paint(ctx, pal) { paintMesa(ctx, pal, m.s, m.seed) } })
            footprints.push({ circle: true, x: m.x, y: m.y - m.s * 0.12, r: m.s * 0.55 }, { circle: true, x: m.x - m.s * 0.55, y: m.y - m.s * 0.05, r: m.s * 0.3 }, { circle: true, x: m.x + m.s * 0.55, y: m.y - m.s * 0.05, r: m.s * 0.3 })
        })
        const inMesa = (x, y, pad) => mesas.some((m) => Math.hypot(m.x - x, (m.y - m.s * 0.1 - y) * 1.3) < m.s * 0.95 + pad)
        // acacias on the plain, palms round the oasis
        const target = Math.round(170 * (w * h * 0.35) / (2000 * 1250) * 0.18)
        for (let i = 0; i < target * 20 && trees.length < target; i++) {
            const x = x0 + 60 + r() * (w - 120), y = y0 + 60 + r() * (h - 120), s = 20 + r() * 12, sd = Math.floor(r() * 1e6), g = n2(x / 500, y / 500)
            if (duneness(x, y) > 0.3 || g < 0.45 || inSea(x, y, 120) || inOasis(x, y, 120) || nearCanyon(x, y, CANYON + 30) || inMesa(x, y, 20) || !clearOf(x, y, s * 0.6) || !spaced(x, y, s)) continue
            const t = { x, y, s, kind: "acacia", seed: sd }
            trees.push(t); note(t)
        }
        if (oasis) {
            for (let i = 0; i < 26; i++) {
                const a = (i / 26) * Math.PI * 2 + r() * 0.2, k = 1.35 + r() * 0.55, s = 17 + r() * 8, sd = Math.floor(r() * 1e6)
                const x = oasis.x + Math.cos(a) * oasis.rx * k, y = oasis.y + Math.sin(a) * oasis.ry * k
                if (!clearOf(x, y, 8) || !spaced(x, y, s * 0.8)) continue
                const t = { x, y, s, kind: "palm", lean: Math.cos(a) * -0.2, seed: sd }
                trees.push(t); note(t)
            }
            for (let i = 0; i < 12; i++) {
                const a = r() * Math.PI * 2, sd = Math.floor(r() * 1e6)
                sprites.push({ x: oasis.x + Math.cos(a) * (oasis.rx + 4), y: oasis.y + Math.sin(a) * (oasis.ry + 4), box: [-14, -34, 14, 4], kind: "reeds", sway: 0.8, paint(ctx, pal) { paintReeds(ctx, pal, 14, sd) } })
            }
        }
        for (let i = trees.length - 1; i >= 0; i--) if (!keep(trees[i])) trees.splice(i, 1)
        trees.forEach((t) => {
            if (t.kind === "palm") sprites.push({ x: t.x, y: t.y, box: [-t.s * 2.2, -t.s * 4.2, t.s * 2.2, 6], kind: "tree", sway: 1.1, seed: t.seed, paint(ctx, pal) { paintPalm(ctx, pal, t.s, t.seed, t.lean) } })
            else sprites.push({ x: t.x, y: t.y, box: [-t.s * 1.8, -t.s * 2.6, t.s * 1.8, 6], kind: "tree", sway: 0.6, seed: t.seed, paint(ctx, pal) { paintAcacia(ctx, pal, t.s, t.seed) } })
            footprints.push({ circle: true, x: t.x, y: t.y - 2, r: t.s * 0.2 + 3 })
        })
        // dry scrub and red rocks
        for (let i = 0, made = 0; i < 3000 && made < Math.round(34 * (w * h) / (2000 * 1250) * 0.5); i++) {
            const x = x0 + 60 + r() * (w - 120), y = y0 + 60 + r() * (h - 120), s = 8 + r() * 10, sd = Math.floor(r() * 1e6), rock = r() < 0.4
            if (inSea(x, y, 110) || inOasis(x, y, 60) || nearCanyon(x, y, CANYON - 20) || inMesa(x, y, 10) || duneness(x, y) > 0.55 + (rock ? 0.2 : 0) || !clearOf(x, y, 6) || !spaced(x, y, s)) continue
            const o = { x, y, s, rock, seed: sd }
            note(o); made++
            if (!keep(o)) continue
            smalls.push(o)
            if (rock) {
                sprites.push({ x, y, box: [-s * 1.4, -s * 1.3, s * 1.4, s * 0.8 + 4], kind: "rock", sway: 0, paint(ctx, pal) { paintRock(ctx, redRockPal(pal), s, sd) } })
                footprints.push({ circle: true, x, y: y - 2, r: s * 0.7 })
            } else sprites.push({ x, y, box: [-s * 1.7, -s * 2.2, s * 1.7, s * 0.8 + 4], kind: "bush", sway: 0.3, paint(ctx, pal) { paintBush(ctx, scrubPal(pal), s, sd) } })
        }
        for (let i = mesas.length - 1; i >= 0; i--) if (!keep({ ...mesas[i], kind: "mountain" })) mesas.splice(i, 1)
        return { sprites, footprints, trees, smalls, mesas }
    }

    function paintShadows(ctx, pal, vis, grown) {
        grown.mesas.forEach((m) => { if (vis(m.x, m.y, m.s * 1.6)) softShadow(ctx, m.x + m.s * 0.35, m.y + 6, m.s * 1.2, m.s * 0.3, pal.shadowAlpha * 1.2, pal.shadow) })
        grown.trees.forEach((t) => { if (vis(t.x, t.y, t.s * 3)) softShadow(ctx, t.x + t.s * 0.5, t.y + 2, t.s * 1.4, t.s * 0.45, pal.shadowAlpha * 1.1, pal.shadow) })
        grown.smalls.forEach((o) => { if (vis(o.x, o.y, o.s * 2)) softShadow(ctx, o.x + o.s * 0.3, o.y + 1, o.s * 1.2, o.s * 0.45, pal.shadowAlpha * 0.9, pal.shadow) })
    }

    // Dry tufts on the plain, and the odd desert flower.
    function paintDetails(ctx, pal, vis, clearOf, inSea) {
        if (!active) return
        const area = (w * h) / (2000 * 1250)
        const r = rng(seed + 809)
        ctx.lineWidth = 1.1
        for (let i = 0; i < 1400 * area; i++) {
            const x = x0 + r() * w, y = y0 + r() * h, ht = 3 + r() * 4, kind = r()
            if (!vis(x, y, 8) || duneness(x, y) > 0.35 || inSea(x, y, 100) || inOasis(x, y, 20) || nearCanyon(x, y, CANYON + 10) || !clearOf(x, y, -14)) continue
            if (kind < 0.03) { ctx.fillStyle = pal.bloom2; ctx.beginPath(); ctx.arc(x, y, 1.8, 0, Math.PI * 2); ctx.fill(); continue }
            ctx.globalAlpha = 0.45
            paintTuft(ctx, kind < 0.5 ? pal.scrub : pal.duneDeep, x, y, ht)
            ctx.globalAlpha = 1
        }
    }

    return { active, inOasis, inWater: (x, y, pad = 0) => inOasis(x, y, pad), nearCanyon, biome, inside, paintLand, paintShore, paintAfterSea, grow, paintShadows, paintDetails }
}

// ---- painters -------------------------------------------------------------------------

const redCache = new Map()
function redRockPal(pal) {
    if (!redCache.has(pal)) redCache.set(pal, { ...pal, stone: pal.redRock, stoneLight: pal.redRockLight, stoneDark: pal.redRockDark, moss: pal.scrub })
    return redCache.get(pal)
}
const scrubCache = new Map()
function scrubPal(pal) {
    if (!scrubCache.has(pal)) scrubCache.set(pal, { ...pal, leaf: pal.scrub, leafDark: mix(pal.scrub, "#000000", 0.25), leafLight: mix(pal.scrub, "#FFFFFF", 0.2), berry: pal.bloom2, flowers: [pal.bloom2] })
    return scrubCache.get(pal)
}

// A crescent dune (a barchan) on the ground: a sunlit windward back swelling
// up to a sharp crest, the steep slip face in shadow inside the curve, and
// the horns trailing away downwind. Ripples run across the back.
function crescent(ctx, pal, x, y, s, tilt) {
    ctx.save()
    ctx.translate(x, y); ctx.rotate(tilt)
    // the back: a soft swell of lighter sand behind the crest
    const back = ctx.createRadialGradient(0, -s * 0.34, s * 0.1, 0, -s * 0.2, s * 1.05)
    back.addColorStop(0, withAlpha(pal.duneLight, 0.85)); back.addColorStop(0.6, withAlpha(pal.duneLight, 0.35)); back.addColorStop(1, withAlpha(pal.duneLight, 0))
    ctx.fillStyle = back
    ctx.beginPath(); ctx.ellipse(0, -s * 0.22, s * 1.05, s * 0.5, 0, 0, Math.PI * 2); ctx.fill()
    // ripples over the back
    ctx.strokeStyle = withAlpha(pal.duneShade, 0.45); ctx.lineWidth = 1
    for (let k = 1; k <= 4; k++) {
        const d = s * (0.12 + k * 0.1)
        ctx.beginPath(); ctx.moveTo(-s * 0.7 + k * s * 0.06, -s * 0.2 - d * 0.6); ctx.quadraticCurveTo(0, -s * 0.52 - d, s * 0.7 - k * s * 0.06, -s * 0.2 - d * 0.6); ctx.stroke()
    }
    // the slip face: shadow inside the crest's curve, darkest right under it
    const face = new Path2D()
    face.moveTo(-s, 0); face.quadraticCurveTo(0, -s * 0.55, s, 0); face.quadraticCurveTo(0, -s * 0.12, -s, 0); face.closePath()
    const shade = ctx.createLinearGradient(0, -s * 0.3, 0, s * 0.02)
    shade.addColorStop(0, withAlpha(mix(pal.duneDeep, pal.redRockDark, 0.2), 1)); shade.addColorStop(1, withAlpha(pal.duneShade, 0.65))
    ctx.fillStyle = shade; ctx.fill(face)
    ctx.beginPath(); ctx.moveTo(-s, 0); ctx.quadraticCurveTo(0, -s * 0.12, s, 0)
    ctx.strokeStyle = withAlpha(pal.duneDeep, 0.5); ctx.lineWidth = 1.2; ctx.stroke()
    // the crest, catching the light, and the horns tapering off
    ctx.beginPath(); ctx.moveTo(-s, 0); ctx.quadraticCurveTo(0, -s * 0.55, s, 0)
    ctx.strokeStyle = withAlpha(pal.duneLight, 1); ctx.lineWidth = 2.6; ctx.lineCap = "round"; ctx.stroke()
    ctx.strokeStyle = withAlpha(pal.ink, 0.25); ctx.lineWidth = 1; ctx.stroke()
    // a pale apron where the face spills out onto the flat
    ctx.beginPath(); ctx.moveTo(-s * 0.85, s * 0.04); ctx.quadraticCurveTo(0, s * 0.16, s * 0.85, s * 0.04)
    ctx.strokeStyle = withAlpha(pal.duneLight, 0.4); ctx.lineWidth = 5; ctx.stroke()
    ctx.restore()
}

// A mesa: a flat-topped butte of banded red rock seen from above and in
// front, so its dusty top shows; lit on the left, gullied down its faces,
// with fallen rock at its foot.
export function paintMesa(ctx, pal, s, seed) {
    const r = rng(seed)
    const ink = pal.ink
    const h = s * (0.55 + r() * 0.25), top = s * (0.45 + r() * 0.2), ty = top * 0.24
    // the body, from the front of the top down to the wider foot
    const outline = [[-s, 0], [-s * 0.82, -h * 0.35], [-top - s * 0.05, -h * 0.82], [-top, -h], [top, -h], [top + s * 0.07, -h * 0.8], [s * 0.85, -h * 0.3], [s, 0]]
    inkPoly(ctx, outline, pal.redRock, ink, { line: 1.9 })
    ctx.save()
    ctx.beginPath(); outline.forEach(([x, y], i) => (i ? ctx.lineTo(x, y) : ctx.moveTo(x, y))); ctx.closePath(); ctx.clip()
    // the shaded right-hand face
    ctx.fillStyle = pal.redRockDark
    ctx.beginPath(); ctx.moveTo(top * 0.4, -h); ctx.lineTo(s * 1.1, -h); ctx.lineTo(s * 1.1, 0); ctx.lineTo(s * 0.35, 0); ctx.closePath(); ctx.fill()
    hatch(ctx, [0, -h, s * 1.1, h], mix(pal.redRockDark, "#000000", 0.3), { seed, angle: -1.2, gap: 3.4, alpha: 0.45 })
    // bands of strata, bowed a little with the face
    for (let i = 1; i < 6; i++) {
        const y = -h + (h * i) / 6 + (r() - 0.5) * 3, bow = 3 + i
        ctx.strokeStyle = withAlpha(i % 2 ? pal.redRockLight : pal.redRockDark, 0.7); ctx.lineWidth = i % 2 ? 2 : 1.2
        ctx.beginPath(); ctx.moveTo(-s, y); ctx.quadraticCurveTo(0, y + bow + (r() - 0.5) * 4, s, y); ctx.stroke()
    }
    // gullies: runnels worn down the faces
    for (let i = 0; i < 7; i++) {
        const gx = (r() - 0.5) * top * 1.8, len = h * (0.4 + r() * 0.5)
        ctx.strokeStyle = withAlpha(mix(pal.redRockDark, "#000000", 0.25), 0.55); ctx.lineWidth = 1.2
        ctx.beginPath(); ctx.moveTo(gx, -h + 2); ctx.quadraticCurveTo(gx + (r() - 0.5) * 8, -h + len * 0.5, gx * 1.15 + (r() - 0.5) * 6, -h + len); ctx.stroke()
    }
    ctx.restore()
    // the flat top: dusty rock with a lit rim and a tuft or two of scrub
    ctx.beginPath(); ctx.ellipse(0, -h, top, ty, 0, 0, Math.PI * 2)
    ctx.fillStyle = mix(pal.redRockLight, pal.dune, 0.35); ctx.fill()
    ctx.strokeStyle = ink; ctx.lineWidth = 1.6; ctx.stroke()
    ctx.save(); ctx.clip()
    ctx.fillStyle = withAlpha(pal.redRockLight, 0.9); ctx.beginPath(); ctx.ellipse(-top * 0.3, -h - ty * 0.35, top * 0.6, ty * 0.45, 0, 0, Math.PI * 2); ctx.fill()
    for (let i = 0; i < 5; i++) { ctx.fillStyle = withAlpha(pal.scrub, 0.9); ctx.beginPath(); ctx.arc((r() - 0.5) * top * 1.4, -h + (r() - 0.5) * ty, 2 + r() * 2.5, 0, Math.PI * 2); ctx.fill() }
    ctx.restore()
    // fallen rock at the foot
    for (let i = 0; i < 14; i++) inkEllipse(ctx, (r() - 0.5) * s * 2, -r() * 8, 3 + r() * 5, 2 + r() * 2.2, r() < 0.5 ? pal.redRock : pal.redRockLight, withAlpha(ink, 0.5), 0.8)
}

// An acacia: a thin forked trunk under a wide, flat, layered crown.
export function paintAcacia(ctx, pal, s, seed) {
    const r = rng(seed)
    const ink = pal.ink
    const top = -s * 2.1
    line(ctx, [[0, 0], [-s * 0.1, top * 0.55], [-s * 0.55, top * 0.92]], ink, 4.4); line(ctx, [[0, 0], [-s * 0.1, top * 0.55], [-s * 0.55, top * 0.92]], pal.bark, 2.8)
    line(ctx, [[-s * 0.1, top * 0.55], [s * 0.45, top * 0.95]], ink, 3.6); line(ctx, [[-s * 0.1, top * 0.55], [s * 0.45, top * 0.95]], pal.bark, 2.2)
    // the crown: two flat layers of small clumps
    ;[[top * 1.02, s * 1.7, pal.acacia], [top * 1.12, s * 1.2, mix(pal.acacia, "#FFFFFF", 0.15)]].forEach(([y, wd, color], layer) => {
        ctx.beginPath()
        ctx.ellipse(0, y, wd, s * 0.32, 0, 0, Math.PI * 2)
        ctx.fillStyle = layer ? color : mix(pal.acacia, "#000000", 0.2); ctx.fill()
        ctx.strokeStyle = ink; ctx.lineWidth = 1.5; ctx.stroke()
        for (let i = 0; i < 14; i++) {
            const x = (r() - 0.5) * wd * 1.8, yy = y + (r() - 0.5) * s * 0.35
            ctx.fillStyle = r() < 0.5 ? mix(color, "#FFFFFF", 0.2) : mix(color, "#000000", 0.15)
            ctx.beginPath(); ctx.arc(x, yy, 2.2 + r() * 2, 0, Math.PI * 2); ctx.fill()
        }
    })
}
